import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Injectable } from '@angular/core';
import { ValidationErrorJsonObject } from 'api';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { SchemaPath, validate } from '@angular/forms/signals';

/*
A "required" validator that also checks for whitespace-only strings.
If the control's value is a string and consists only of whitespace, it returns a validation error
with the key "required".
Otherwise, it delegates to the built-in Validators.required function.
*/
export function requiredAndNotWhitespaceOnly(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  return typeof value === 'string' && value.trim().length === 0
    ? { required: true }
    : Validators.required(control);
}

export function requiredAndNotWhitespaceOnlySignal(path: SchemaPath<string>): void {
  validate(path, ({ value }) => {
    const currentValue = value();

    return typeof currentValue === 'string' && currentValue.trim().length === 0
      ? { kind: 'required' }
      : null;
  });
}

/*
Use this when you want to display errors from synchronous validators already when the form control
became "dirty" (after the first keystroke for text inputs)
*/
export const dirtyErrorStateMatcher: ErrorStateMatcher = {
  isErrorState: (
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean => {
    return !!control?.invalid && (control.dirty || !!form?.submitted);
  },
};

@Injectable({
  providedIn: 'root',
})
export class FormService {
  public clearError(form: FormGroup, controlName: string, validatorName: string): void {
    const control = form.get(controlName);

    if (!control?.hasError(validatorName)) {
      return;
    }

    const { [validatorName]: _, ...remainingErrors } = control.errors ?? {};
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  public showValidationErrors(form: FormGroup, error: ValidationErrorJsonObject): void {
    if (error.validatorName) {
      error.fieldNames?.forEach((fieldName) => {
        const control = form.get(fieldName);
        if (control) {
          control.setErrors({ ...control.errors, [error.validatorName!]: true });
        }
      });
    }
  }

  public async runAsyncValidator(
    form: FormGroup,
    controlName: string,
    validatorNames: string[],
    validator: () => Observable<any>,
  ) {
    const control = form.get(controlName) as FormControl<string> | null;

    if (!control || control.invalid) {
      return;
    }
    const value = control.value;
    try {
      await firstValueFrom(validator());
      if (control.value !== value) {
        return;
      }
      validatorNames.forEach((validatorName) => {
        this.clearError(form, controlName, validatorName);
      });
    } catch (e) {
      this.showValidationErrors(form, (e as HttpErrorResponse).error);
    }
  }
}
